'use client';

import { Fragment } from 'react';

import classnames from 'classnames';

import type { RankingProps } from './Ranking.types';
import { ReactComponent as BronzeMedalImg } from './icons/bronze-medal';
import { ReactComponent as GoldMedalImg } from './icons/gold-medal';
import { ReactComponent as SilverMedalImg } from './icons/silver-medal';

import styles from './Ranking.module.scss';

const MAX_CELL_SIZE = 20;

/** Only the podium is drawn; every other place is written out as its number. */
const MEDALS: Partial<Record<number, typeof GoldMedalImg>> = {
  1: GoldMedalImg,
  2: SilverMedalImg,
  3: BronzeMedalImg,
};

function Ranking({
  style,
  className,
  columns,
  rows,
  footer,
  testId = 'ui-ranking',
}: RankingProps) {
  // A row only counts off the table when it does not say where it placed: a
  // board that appends the reader under its top few needs their real position,
  // which their place in `rows` no longer gives.
  const positions = rows.map((row, index) => row.position ?? index + 1);

  return (
    <table
      style={style}
      className={classnames(styles['ranking'], className)}
      data-testid={testId}
    >
      <thead className={styles['head']}>
        <tr>
          <th></th>
          {columns.map((column, index) => (
            <th key={index}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody className={styles['body']}>
        {rows.map((row, index) => {
          const position = positions[index]!;
          const Medal = MEDALS[position];

          // The places the board skipped over are drawn as a row of their own,
          // so the reader appended under the top few reads as standing far down
          // the field rather than as the one who came next.
          const skipped = index > 0 && position > positions[index - 1]! + 1;

          return (
            <Fragment key={index}>
              {skipped && (
                <tr className={styles['gap']} aria-hidden="true">
                  <td
                    className={styles['gap-cell']}
                    colSpan={columns.length + 1}
                  >
                    …
                  </td>
                </tr>
              )}
              <tr
                className={classnames(
                  styles['row'],
                  row.highlighted && styles['row--highlighted'],
                  row.variant && styles[`row--${row.variant}`],
                )}
              >
                <td className={styles['position']}>
                  <div className={styles['position-wrapper']}>
                    {/* Height only: the medals are square, and a width in per
                        cent would be read against a column that has no width of
                        its own to give. */}
                    {Medal ? <Medal height="100%" /> : position}
                  </div>
                </td>
                {row.cells.map((cell, index) => (
                  <td className={styles['td']} key={index}>
                    {cell.length > MAX_CELL_SIZE
                      ? cell.substring(0, MAX_CELL_SIZE) + '…'
                      : cell}
                  </td>
                ))}
              </tr>
            </Fragment>
          );
        })}
      </tbody>
      {footer !== undefined && (
        <tfoot className={styles['foot']}>
          <tr>
            <td className={styles['foot-cell']} colSpan={columns.length + 1}>
              <div className={styles['foot-line']}>{footer}</div>
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

export { Ranking };
